import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  GeoPoint,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { firestore } from "../firebase";
import {
  mapPlaceDocumentToPlace,
  Place,
  PlaceDataSubmission,
  PlaceDocument,
  ResolvedPlaceDataSubmission,
} from "../types/Place";
import { PlaceDataSubmissionStatus } from "../types/PlaceDataSubmissionStatus";
import { debugLogError } from "../utils/debug-log-error";
import { getUserProfilesByIds } from "./userProfileService";

const PLACES_COLLECTION = "places";
const PLACE_DATA_SUBMISSIONS_COLLECTION = "placeDataSubmissions";

/**
 * Fetches the most recently created places from Firestore.
 *
 * @param count The number of recent places to fetch.
 * @returns A promise that resolves to an array of Place objects.
 */
export const getRecentPlaces = async (count: number): Promise<Place[]> => {
  try {
    const placesCollectionRef = collection(firestore, PLACES_COLLECTION);
    const q = query(
      placesCollectionRef,
      orderBy("createdAt", "desc"),
      limit(count)
    );

    const querySnapshot = await getDocs(q);
    const places: Place[] = [];
    querySnapshot.forEach((document) => {
      const data = document.data() as PlaceDocument;
      const place: Place = mapPlaceDocumentToPlace(data);
      places.push(place);
    });

    return places;
  } catch (error) {
    debugLogError("getRecentPlaces", error);
    return [];
  }
};

/**
 * Approves a place data submission.
 *
 * @param submissionId The ID of the submission to approve.
 * @param placeId The ID of the place to update.
 * @param submissionData The data from the submission to merge into the place.
 * @returns A promise that resolves to void.
 */
export const approvePlaceDataSubmission = async (
  submissionId: string,
  placeId: string,
  submissionData: Partial<PlaceDataSubmission>
): Promise<void> => {
  try {
    const place = await getPlaceById(placeId);
    if (!place) {
      throw new Error(`Place with ID ${placeId} not found.`);
    }

    const submissionDocRef = doc(
      firestore,
      PLACE_DATA_SUBMISSIONS_COLLECTION,
      submissionId
    );

    const updatedPlaceData: Partial<Place> = { ...place };

    // Merge fields from submissionData
    // These are fields that can be directly copied or overwritten if present in submissionData
    if (submissionData.numberOfTables !== undefined) {
      updatedPlaceData.numberOfTables = submissionData.numberOfTables;
    }
    if (submissionData.vibes !== undefined) {
      updatedPlaceData.vibes = submissionData.vibes;
    }
    if (submissionData.vibeSummary !== undefined) {
      updatedPlaceData.vibeSummary = submissionData.vibeSummary;
    }
    if (submissionData.paymentTypes !== undefined) {
      updatedPlaceData.paymentTypes = submissionData.paymentTypes;
    }
    if (submissionData.status !== undefined) {
      updatedPlaceData.status = submissionData.status;
    }

    const finalUpdatePayload: Partial<Place> = {
      ...updatedPlaceData,
      updatedAt: Timestamp.now(),
    };

    // Run a transaction to update the place and the submission:
    await runTransaction(firestore, async (transaction) => {
      const placeDocRef = doc(firestore, PLACES_COLLECTION, placeId);

      transaction.update(submissionDocRef, {
        submissionStatus: PlaceDataSubmissionStatus.ACCEPTED,
        updatedAt: serverTimestamp(),
      });

      transaction.update(placeDocRef, finalUpdatePayload);
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Rejects a place data submission.
 *
 * @param submissionId The ID of the submission to reject.
 * @returns A promise that resolves to void.
 */
export const rejectPlaceDataSubmission = async (
  submissionId: string
): Promise<void> => {
  try {
    const submissionDocRef = doc(
      firestore,
      PLACE_DATA_SUBMISSIONS_COLLECTION,
      submissionId
    );
    await updateDoc(submissionDocRef, {
      submissionStatus: PlaceDataSubmissionStatus.REJECTED,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches all places from Firestore.
 *
 * @returns A promise that resolves to an array of Place objects.
 */
export const getAllPlaces = async (): Promise<Place[]> => {
  try {
    const placesCollectionRef = collection(firestore, PLACES_COLLECTION);
    const querySnapshot = await getDocs(placesCollectionRef);
    const places: Place[] = [];
    querySnapshot.forEach((document) => {
      const data = document.data() as PlaceDocument;
      const place: Place = mapPlaceDocumentToPlace(data);
      places.push(place);
    });
    return places;
  } catch (error) {
    console.error("Error fetching all places:", error);
    return [];
  }
};

/**
 * Fetches a specific place by its ID from Firestore.
 *
 * @param id The ID of the place to fetch.
 * @returns A promise that resolves to a Place object or null if not found.
 */
export const getPlaceById = async (id: string): Promise<Place | null> => {
  try {
    const placeDocRef = doc(firestore, PLACES_COLLECTION, id);
    const docSnap = await getDoc(placeDocRef);

    if (docSnap.exists()) {
      const data = docSnap.data() as PlaceDocument;
      return mapPlaceDocumentToPlace(data);
    } else {
      return null;
    }
  } catch (error) {
    debugLogError("getPlaceById", error);
    return null;
  }
};

type PlaceMap = Record<string, Place>;
type BatchPlaceFetchResult = {
  placeMap: PlaceMap;
  places: Place[];
};

export async function getPlacesByIds(
  placeIds: string[]
): Promise<BatchPlaceFetchResult> {
  const places: Place[] = [];
  const placeMap: PlaceMap = {};

  for (const placeId of placeIds) {
    const place = await getPlaceById(placeId);
    if (place) {
      places.push(place);
      placeMap[placeId] = place;
    }
  }

  return { placeMap, places };
}

/**
 * Updates a place in Firestore.
 *
 * @param id The ID of the place to update.
 * @param data The partial data to update the place with.
 * @returns A promise that resolves to void.
 */
export const updatePlace = async (
  id: string,
  data: Partial<Place>
): Promise<void> => {
  try {
    const placeDocRef = doc(firestore, PLACES_COLLECTION, id);
    // Prepare data for Firestore: convert lat/lng back to GeoPoint if present
    const firestoreData: Partial<PlaceDocument> = { ...data };
    if (data.lat !== undefined && data.lng !== undefined) {
      firestoreData.location = new GeoPoint(data.lat, data.lng);
      delete (firestoreData as Partial<Place>).lat; // remove lat and lng from top level
      delete (firestoreData as Partial<Place>).lng;
    } else if (data.lat !== undefined || data.lng !== undefined) {
      // If only one is provided, it's a bit ambiguous.
      // Firestore GeoPoint needs both.
      // Consider fetching the document to get the other coordinate or throwing an error.
      // For now, let's log a warning and not update location.
      console.warn(
        "Both lat and lng must be provided to update location. Location will not be updated."
      );
      delete (firestoreData as Partial<Place>).lat;
      delete (firestoreData as Partial<Place>).lng;
    }

    await updateDoc(placeDocRef, firestoreData);
  } catch (error) {
    console.error("Error updating place:", error);
    // Rethrow or handle as per application needs
    throw error;
  }
};

/**
 * Deletes a place and all associated place data submissions from Firestore.
 *
 * @param id The ID of the place to delete.
 * @returns A promise that resolves to void.
 */
export const deletePlace = async (id: string): Promise<void> => {
  try {
    // Delete the place:
    const placeDocRef = doc(firestore, PLACES_COLLECTION, id);
    await deleteDoc(placeDocRef);

    // Delete all place data submissions for this place:
    const submissionsCollectionRef = collection(
      firestore,
      PLACE_DATA_SUBMISSIONS_COLLECTION
    );
    const q = query(submissionsCollectionRef, where("placeId", "==", id));
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((document) => {
      deleteDoc(doc(firestore, PLACE_DATA_SUBMISSIONS_COLLECTION, document.id));
    });
  } catch (error) {
    debugLogError("placeService: Error deleting place:", error);
    throw error;
  }
};

/** Creates a new place data submission. */
export const createPlaceDataSubmission = async (
  data: Partial<PlaceDataSubmission>
): Promise<void> => {
  const placeDataSubmissionDocRef = collection(
    firestore,
    PLACE_DATA_SUBMISSIONS_COLLECTION
  );
  await addDoc(placeDataSubmissionDocRef, {
    ...data,
    submissionStatus: PlaceDataSubmissionStatus.PENDING,
    createdAt: serverTimestamp(),
  });
};

export async function getPlaceDataSubmissionsForPlace(
  placeId: string
): Promise<PlaceDataSubmission[]> {
  const submissionsCollectionRef = collection(
    firestore,
    PLACE_DATA_SUBMISSIONS_COLLECTION
  );
  const q = query(submissionsCollectionRef, where("placeId", "==", placeId));
  const querySnapshot = await getDocs(q);
  const submissions: PlaceDataSubmission[] = [];
  querySnapshot.forEach((document) => {
    const data = document.data() as PlaceDataSubmission;
    submissions.push(data);
  });
  return submissions;
}

/**
 * Fetches all place data submissions from Firestore.
 *
 * @returns A promise that resolves to an array of PlaceDataSubmission objects.
 */
export const getPlaceDataSubmissions = async (
  onlyShowToReview: boolean
): Promise<ResolvedPlaceDataSubmission[]> => {
  try {
    const submissionsCollectionRef = collection(
      firestore,
      PLACE_DATA_SUBMISSIONS_COLLECTION
    );
    // If onlyShowToReview is true, only show submissions that are under review or pending:
    const q = onlyShowToReview
      ? query(
          submissionsCollectionRef,
          where("submissionStatus", "in", [
            PlaceDataSubmissionStatus.PENDING,
            PlaceDataSubmissionStatus.UNDER_REVIEW,
          ])
        )
      : query(submissionsCollectionRef, orderBy("createdAt", "desc"));

    const userIds: string[] = [];
    const placeIds: string[] = [];

    const querySnapshot = await getDocs(q);
    const submissions: ResolvedPlaceDataSubmission[] = [];
    querySnapshot.forEach((document) => {
      const data = document.data();
      userIds.push(data.creatorId);
      placeIds.push(data.placeId);

      submissions.push({
        id: document.id,
        place: null,
        placeId: data.placeId,
        creator: null,
        creatorId: data.creatorId,
        confirmedUserIds: data.confirmedUserIds,
        rejectedUserIds: data.rejectedUserIds,
        flaggedUserIds: data.flaggedUserIds,
        createdAt: data.createdAt?.toDate(),
        submissionStatus: data.submissionStatus,
        numberOfTables: data.numberOfTables,
        vibes: data.vibes,
        vibeSummary: data.vibeSummary,
        paymentTypes: data.paymentTypes,
        status: data.status,
      });
    });

    const { userProfileMap } = await getUserProfilesByIds(userIds);
    const { placeMap } = await getPlacesByIds(placeIds);

    submissions.forEach((submission) => {
      submission.creator = userProfileMap[submission.creatorId];
      submission.place = placeMap[submission.placeId];
    });

    return submissions;
  } catch (error) {
    console.error("Error fetching place data submissions:", error);
    return [];
  }
};
